/** Measured download via S4 `/distribution/netcheck/download` (not movie media URL).
 * Cached per movieId for the tab so remounts do not re-probe. */

export const MOVIES_SPEEDTEST_BYTES = 2 * 1024 * 1024;

const SESSION_CACHE_KEY = 'movies_tp_v1';
const throughputByMovieId = new Map<string, number>();
const inflightByMovieId = new Map<string, Promise<number>>();

function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function bytesToKbps(bytes: number, elapsedMs: number): number {
  if (!(bytes > 0) || !(elapsedMs >= 200)) return 0;
  const kbps = Math.round((bytes * 8) / elapsedMs);
  return kbps > 0 ? Math.min(500_000, kbps) : 0;
}

function readSessionCache(movieId: string): number {
  try {
    if (typeof sessionStorage === 'undefined') return 0;
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return 0;
    const o = JSON.parse(raw) as Record<string, number>;
    const v = Number(o?.[movieId]);
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

function writeSessionCache(movieId: string, kbps: number) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    const o = (raw ? JSON.parse(raw) : {}) as Record<string, number>;
    o[movieId] = kbps;
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(o));
  } catch {
    /* ignore */
  }
}

export function peekMoviesThroughputKbps(movieId: string): number {
  const id = String(movieId || '').trim();
  if (!id) return 0;
  const mem = Number(throughputByMovieId.get(id));
  if (Number.isFinite(mem) && mem > 0) return mem;
  const fromSession = readSessionCache(id);
  if (fromSession > 0) {
    throughputByMovieId.set(id, fromSession);
    return fromSession;
  }
  return 0;
}

function remember(movieId: string, kbps: number) {
  const id = String(movieId || '').trim();
  if (!id || !(kbps > 0)) return;
  throughputByMovieId.set(id, kbps);
  writeSessionCache(id, kbps);
}

/**
 * Timed first→last byte netcheck download. Returns kbps or 0.
 * Pass movieId to measure at most once per movie in this tab.
 */
export async function measureMoviesThroughputKbps(opts: {
  movieId?: string;
  apiBase: string;
  bytes?: number;
  signal?: AbortSignal;
}): Promise<number> {
  const id = String(opts.movieId || '').trim();
  const cached = peekMoviesThroughputKbps(id);
  if (cached > 0) return cached;

  if (id && inflightByMovieId.has(id)) {
    try {
      return await inflightByMovieId.get(id)!;
    } catch {
      return 0;
    }
  }

  const run = runNetcheckProbe(opts).then((kbps) => {
    if (kbps > 0 && id) remember(id, kbps);
    return kbps;
  });
  if (id) {
    inflightByMovieId.set(id, run);
    run.finally(() => {
      if (inflightByMovieId.get(id) === run) inflightByMovieId.delete(id);
    });
  }
  return run;
}

async function runNetcheckProbe(opts: {
  apiBase: string;
  bytes?: number;
  signal?: AbortSignal;
}): Promise<number> {
  const base = String(opts.apiBase || '').trim().replace(/\/+$/, '');
  if (!base) return 0;
  const want = Math.max(256 * 1024, Math.floor(opts.bytes || MOVIES_SPEEDTEST_BYTES));
  const url = `${base}/distribution/netcheck/download?bytes=${want}&n=${Date.now()}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: opts.signal,
    });
  } catch {
    return 0;
  }
  if (res.status === 429 || !(res.status === 200 || res.status === 206)) return 0;

  const reader = res.body?.getReader?.();
  if (!reader) {
    const t0 = nowMs();
    try {
      const buf = await res.arrayBuffer();
      return bytesToKbps(buf.byteLength, nowMs() - t0);
    } catch {
      return 0;
    }
  }

  let received = 0;
  let tFirst = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = value?.byteLength || 0;
      if (chunk <= 0) continue;
      if (!tFirst) tFirst = nowMs();
      received += chunk;
      if (received >= want) {
        try {
          await reader.cancel();
        } catch {
          /* ignore */
        }
        break;
      }
    }
  } catch {
    return 0;
  }
  if (received < want * 0.5) return 0;
  return bytesToKbps(received, nowMs() - (tFirst || nowMs()));
}
